import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import ThemeToggle from '../components/ui/ThemeToggle';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../layouts/MainLayout';
import { STORAGE_KEYS, formatDuration, formatDate } from '../config';
import ApiService from '../services/api';

interface DashboardStats {
  total_users: number;
  total_agents: number;
  total_groups: number;
  total_modules: number;
  total_calls: number;
  total_contacts: number;
  completed_calls: number;
  missed_calls: number;
  total_call_duration: number;
  average_call_duration: number;
  today_calls: number;
  this_week_calls: number;
  this_month_calls: number;
}

interface CachedStats {
  data: DashboardStats;
  timestamp: number;
}

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

const Dashboard: React.FC = () => {
  const { user, logout, canAccessModule, getUserPermissions, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const permissions = getUserPermissions();

  useEffect(() => {
    fetchStats();
  }, [token]);

  const getCachedStats = (): CachedStats | null => {
    try {
      const cached = localStorage.getItem(STORAGE_KEYS.DASHBOARD_STATS);
      if (cached) {
        const parsed: CachedStats = JSON.parse(cached);
        const now = Date.now();
        if (now - parsed.timestamp < CACHE_DURATION) {
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error reading cached stats:', error);
    }
    return null;
  };

  const setCachedStats = (data: DashboardStats) => {
    try {
      const cached: CachedStats = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(STORAGE_KEYS.DASHBOARD_STATS, JSON.stringify(cached));
    } catch (error) {
      console.error('Error caching stats:', error);
    }
  };

  const fetchStats = async () => {
    if (!token) return;

    // Check cache first
    const cached = getCachedStats();
    if (cached) {
      setStats(cached.data);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await ApiService.get<DashboardStats>('dashboard/stats');
      const statsData = response.data;
      setStats(statsData);
      setCachedStats(statsData);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setError(error instanceof Error ? error.message : 'Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    return num?.toLocaleString() || '0';
  };

  const handleRefreshStats = () => {
    // Clear cache and refetch
    localStorage.removeItem(STORAGE_KEYS.DASHBOARD_STATS);
    fetchStats();
  };

  return (
    <MainLayout>
      <div className="p-6">
        {/* Page Header */}
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Welcome back, {user?.name}! Here's what's happening today.
            </p>
          </div>
          <Button
            onClick={handleRefreshStats}
            disabled={loading}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                Refreshing...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </>
            )}
          </Button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error loading dashboard data
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Info */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            User Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Name</p>
              <p className="text-gray-900 dark:text-white">{user?.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Username</p>
              <p className="text-gray-900 dark:text-white">{user?.username}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
              <p className="text-gray-900 dark:text-white">{user?.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">User Type</p>
              <p className="text-gray-900 dark:text-white capitalize">{user?.user_type}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Group</p>
              <p className="text-gray-900 dark:text-white">{user?.group?.title || 'No Group'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Permissions</p>
              <p className="text-gray-900 dark:text-white">{permissions.length} modules</p>
            </div>
          </div>
        </div>

        {/* Statistics Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Main Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">U</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Total Users
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {formatNumber(stats?.total_users || 0)}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">A</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Total Agents
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {formatNumber(stats?.total_agents || 0)}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">C</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Total Calls
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {formatNumber(stats?.total_calls || 0)}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">T</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Today's Calls
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {formatNumber(stats?.today_calls || 0)}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Call Analytics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">✓</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Completed Calls
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {formatNumber(stats?.completed_calls || 0)}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">✗</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Missed Calls
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {formatNumber(stats?.missed_calls || 0)}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">⏱</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Total Duration
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {formatDuration(stats?.total_call_duration || 0)}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-indigo-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">📞</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Avg Duration
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {formatDuration(Math.round(stats?.average_call_duration || 0))}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">📅</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          This Week
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {formatNumber(stats?.this_week_calls || 0)} calls
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-pink-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">📊</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          This Month
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {formatNumber(stats?.this_month_calls || 0)} calls
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-teal-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">👥</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Total Contacts
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {formatNumber(stats?.total_contacts || 0)}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {canAccessModule('users') && (
            <div
              className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate('/users')}
            >
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  User Management
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Manage users, permissions, and access control
                </p>
                <div className="mt-4 flex flex-wrap gap-1">
                  {permissions.find(p => p.module_route === 'users')?.can_add && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Add</span>
                  )}
                  {permissions.find(p => p.module_route === 'users')?.can_edit && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">Edit</span>
                  )}
                  {permissions.find(p => p.module_route === 'users')?.can_delete && (
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">Delete</span>
                  )}
                  {permissions.find(p => p.module_route === 'users')?.can_export && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">Export</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {canAccessModule('groups') && (
            <div
              className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate('/groups')}
            >
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Group Management
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Organize users into groups and manage permissions
                </p>
                <div className="mt-4 flex flex-wrap gap-1">
                  {permissions.find(p => p.module_route === 'groups')?.can_add && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Add</span>
                  )}
                  {permissions.find(p => p.module_route === 'groups')?.can_edit && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">Edit</span>
                  )}
                  {permissions.find(p => p.module_route === 'groups')?.can_delete && (
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">Delete</span>
                  )}
                  {permissions.find(p => p.module_route === 'groups')?.can_export && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">Export</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {canAccessModule('modules') && (
            <div
              className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate('/modules')}
            >
                <div
                    className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => navigate('/reports')}
                >
                    <div className="p-6">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            Call Reports
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                            View call analytics and performance metrics
                        </p>
                        <div className="mt-4 flex flex-wrap gap-1">
                            <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">Analytics</span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">Reports</span>
                        </div>
                    </div>
                </div>
            </div>
          )}


        </div>

        {/* No Permissions Message */}
        {permissions.length === 0 && (
          <div className="mt-8 bg-white dark:bg-gray-800 shadow rounded-lg p-6 text-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Module Access
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              You don't have permission to access any modules. Please contact your administrator.
            </p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Dashboard;
